import { useEffect, useState } from "react";
import { X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const DriverEarningsModal = ({ driver, onClose }) => {
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/drivers/${driver.id}/earnings`, { params: { month } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load earnings."))
      .finally(() => setLoading(false));
  }, [driver.id, month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthKey(new Date(y, m - 1 + delta, 1)));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="driver-earnings-modal">
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0a0a0a] p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Driver Records · Revenue Breakdown</p>
            <h3 className="text-lg text-white font-medium mt-1">{driver.name}</h3>
          </div>
          <button onClick={onClose} data-testid="earnings-modal-close"
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => shiftMonth(-1)} data-testid="earnings-month-prev"
            className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-white tabular-nums px-2" data-testid="earnings-month-label">{month}</span>
          <button onClick={() => shiftMonth(1)} data-testid="earnings-month-next"
            className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-3">
                <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Total revenue</p>
                <p className="text-lg text-white tabular-nums mt-1" data-testid="earnings-gross">{eur(data?.gross_total)}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">{data?.ride_count || 0} rides</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-3">
                <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Company (20%)</p>
                <p className="text-lg text-zinc-300 tabular-nums mt-1" data-testid="earnings-commission">{eur(data?.commission_amount)}</p>
              </div>
              <div className="rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/5 p-3">
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">Driver share (80%)</p>
                <p className="text-lg text-[#C5A059] tabular-nums mt-1" data-testid="earnings-share">{eur(data?.driver_share)}</p>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Completed rides — {month}</p>
              {!(data?.rides || []).length && (
                <p className="text-sm text-zinc-600" data-testid="earnings-empty">No completed rides this month.</p>
              )}
              {(data?.rides || []).map((r, i) => (
                <div key={r.ref || i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#0d0d0d] px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{r.pickup_location} → {r.dropoff_location || "—"}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{r.pickup_date} · {r.pickup_time} · {r.ref}</p>
                  </div>
                  <p className="text-sm text-zinc-200 tabular-nums">{eur(r.quote_amount)}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverEarningsModal;
